// React-related imports
import { useContext } from 'react';

// Contexts and Hooks
import { Context } from '../../../routes/PrivateRoutes';
import { useCatalog, useMappingSessionAPI } from '../../../services/api';

// Types
import { AutowriteRequestData, DocumentTabs } from '../../../types';
import { IStartAuthoringProps } from './types';

// Others
import { cloneDeep } from 'lodash';
import { notifyError } from '../../../helpers/utils';
import { AuthoringFormData } from '../../addSource/components/modals/AuthoringModal/types';

interface StartAuthoringContextValue {
  setMultipleFile: (files: unknown) => void;
  setTemplateFile: (file: unknown) => void;
  setTemplateFileDisplay: (file: unknown) => void;
}
import { useNavigate } from 'react-router-dom';

export const useStartAuthoring = ({
  selectedRecords,
  sessionId,
  openNonInteractiveDocumentModal,
  templateType,
  closeAuthoringModal,
  nonInteractiveDocuments,
  useLLM,
}: IStartAuthoringProps) => {
  const { autowriteMappingSession, manuallyAuthorMappingSession } = useMappingSessionAPI();
  const { mutateAsync: autowrite, isPending: isAutowritePending } = autowriteMappingSession;
  const { mutateAsync: manualAuthor, isPending: isManualAuthorPending } =
    manuallyAuthorMappingSession;

  const { getCatalogFileData } = useCatalog();
  const navigate = useNavigate();
  const { mutateAsync: getCatalogData, isPending: isCatalogDataPending } = getCatalogFileData;

  const { setMultipleFile, setTemplateFile, setTemplateFileDisplay } = useContext(
    Context,
  ) as StartAuthoringContextValue;

  const isPending = isAutowritePending || isCatalogDataPending || isManualAuthorPending;

  const executeAutowriteRequest = (
    data: Omit<AutowriteRequestData, 'automatedDocId' | 'sourceIds'>,
  ) => {
    if (sessionId && selectedRecords.length) {
      autowrite({
        automatedDocId: sessionId,
        sourceIds: selectedRecords,
        ...data,
      });
      if (nonInteractiveDocuments.length) openNonInteractiveDocumentModal();
    } else {
      notifyError('Select required files');
    }
  };

  const executeManualAuthoringRequest = async (
    data: Omit<AutowriteRequestData, 'automatedDocId' | 'sourceIds'>,
  ) => {
    if (selectedRecords.length && sessionId) {
      const catalogFileParams = {
        sourceFileIds: selectedRecords,
        targetFileId: sessionId,
      };
      const res = await getCatalogData(catalogFileParams);
      setTemplateFile(cloneDeep(res.data.targetFile));
      setTemplateFileDisplay(cloneDeep(res.data.targetFile));
      setMultipleFile(cloneDeep(res.data.sourceFile));
      const response = await manualAuthor({ ...catalogFileParams, ...data, useLLM });
      if (!useLLM) {
        navigate(`/mapping-sessions/${response.data.id}`);
      } else {
        navigate(`/catalog`, { state: { activeTab: DocumentTabs.IN_PROGRESS } });
      }
    } else {
      notifyError('Please select valid files');
    }
  };

  const handleStartAuthoring = (
    data: AuthoringFormData,
    templateVariables?: Array<Record<string, string>>,
  ) => {
    const authoringData = {
      title: data.documentName,
      contentSpecifics: '',
      ctdSection: data.section ?? '',
      program: '',
      version: Number(data.version),
      detailedNotes: '',
      isExternalDoc: templateType === DocumentTabs.EXTERNAL,
      modelType: data.modelType,
      templateVariables: templateVariables || [],
    };

    templateType === DocumentTabs.TARGET
      ? executeManualAuthoringRequest(authoringData)
      : executeAutowriteRequest(authoringData);

    closeAuthoringModal();
  };

  return {
    handleStartAuthoring,
    isPending,
  };
};
